import React, { useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Fab,
  LinearProgress,
  TextField,
  Typography
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import axios from "axios";
import api from "../common/api";

const CreditNotes = ({ client_id }) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [invoiceDateRange, setInvoiceDateRange] = useState({
    from: "",
    to: ""
  });
  const [filters, setFilters] = useState([
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    ["sap error", "pending"],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    []
  ]);
  const [orderColumn, setOrderColumn] = useState("");
  const [orderOrder, setOrderOrder] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const getMuiTheme = () =>
    createMuiTheme({
      overrides: {
        MuiTableCell: {
          root: {
            padding: "0.25rem",
            fontSize: "0.8rem",
            maxWidth: "10rem"
          }
        }
      }
    });

  const fetchData = async (page, filterList, column, order, q_rows) => {
    setIsLoading(true);
    let url = api.credit_notes().index(page + 1, "", "", column, order, q_rows);
    const hasFilter = index =>
      Array.isArray(filterList[index]) && filterList[index].length !== 0;

    if (hasFilter(1)) {
      url += `&sap_code=${filterList[1].join(",")}`;
    }
    if (hasFilter(2)) {
      url += `&number=${filterList[2].join(",")}`;
    }
    if (hasFilter(4)) {
      url += `&invoice.number=${filterList[4].join(",")}`;
    }
    if (hasFilter(5)) {
      const [from, to] = filterList[5];
      if (from) url += `&invoice_date_from=${from}`;
      if (to) url += `&invoice_date_to=${to}`;
    }
    if (hasFilter(6) || hasFilter(9)) {
      const clientName = [
        ...(filterList[6] || []),
        ...(filterList[9] || [])
      ].join(",");
      url += `&client.business_name=${clientName}`;
    }
    if (hasFilter(7)) {
      url += `&status=${filterList[7].join(",")}`;
    }
    if (hasFilter(8)) {
      url += `&client.sap_code=${filterList[8].join(",")}`;
    }
    if (hasFilter(10)) {
      url += `&auxiliary_invoice_code=${filterList[10].join(",")}`;
    }
    if (hasFilter(11)) {
      url += `&level_1=${filterList[11].join(",")}`;
    }
    if (hasFilter(12)) {
      url += `&level_2=${filterList[12].join(",")}`;
    }
    if (hasFilter(13)) {
      url += `&cae=${filterList[13].join(",")}`;
    }
    if (client_id) {
      url += `&client_id=${client_id}`;
    }

    const token = localStorage.getItem("userToken");
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data.data);
      setCount(res.data.total);
      setRowsPerPage(res.data.per_page);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, filters, orderColumn, orderOrder, rowsPerPage);
  }, [page, filters, orderColumn, orderOrder, rowsPerPage]);

  const columns = [
    {
      name: "id",
      label: "ID",
      options: {
        filter: false,
        sort: true,
        sortDirection: orderColumn === "id" ? orderOrder : null
      }
    },
    {
      name: "sap_code",
      label: "Código SAP",
      options: {
        filter: true,
        filterType: "textField",
        sort: false
      }
    },
    {
      name: "credit_note_number",
      label: "Número",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "invoice_number" ? orderOrder : null
      }
    },
    {
      name: "invoice.id",
      label: "ID factura",
      options: {
        display: false,
        viewColumns: false,
        filter: false
      }
    },
    {
      name: "invoice.number",
      label: "Número factura",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        sortDirection: orderColumn === "invoice_number" ? orderOrder : null,
        customBodyRender: (value, tableMeta) => {
          const invoiceId = tableMeta.rowData[2];
          const auxiliarySapCode = tableMeta.rowData[10];
          if (!value) {
            return <span>Cod SAP: {auxiliarySapCode}</span>;
          }
          return (
            <a
              href={`/invoice/${invoiceId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </a>
          );
        }
      }
    },
    {
      name: "date",
      label: "Fecha de documento",
      options: {
        filter: true,
        filterType: "custom",
        filterOptions: {
          display: (filterList, onChange, index, column) => (
            <div>
              <TextField
                label="Fecha desde"
                InputLabelProps={{
                  shrink: true
                }}
                type="date"
                value={filterList[index][0] || ""}
                onChange={event => {
                  filterList[index][0] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
              <TextField
                label="Fecha hasta"
                type="date"
                InputLabelProps={{
                  shrink: true
                }}
                value={filterList[index][1] || ""}
                onChange={event => {
                  filterList[index][1] = event.target.value;
                  onChange(filterList[index], index, column);
                }}
                style={{ width: "45%" }}
              />
            </div>
          )
        },
        customFilterListRender: v => {
          if (!v || v.length === 0) return null;
          const [from, to] = v;
          return [`Desde: ${from || "..."}`, `Hasta: ${to || "..."}`];
        },
        onFilterChipClose: () => {
          setInvoiceDateRange({ from: "", to: "" });
          const newFilterList = [...filters];
          newFilterList[5] = [];
          setFilters(newFilterList);
        },
        sort: true,
        sortDirection: orderColumn === "date" ? orderOrder : null
      }
    },
    {
      name: "client.business_name",
      label: "Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const clientBusinessName = tableMeta.rowData[9];
          return <span>{value ? value : clientBusinessName}</span>;
        }
      }
    },
    {
      name: "status",
      label: "Estado",
      options: {
        filter: true,
        display: false,
        filterType: "select",
        filterOptions: {
          names: ["pending", "sap error", "generated", "arca error"]
        },
        sort: false
      }
    },
    {
      name: "client.sap_code",
      label: "Cod SAP Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "client.business_name",
      label: "Cliente",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false,
        viewColumns: false
      }
    },
    {
      name: "auxiliary_invoice_code",
      label: "Cod. SAP Auxiliar",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false,
        viewColumns: false
      }
    },
    {
      name: "level1.name",
      label: "Nivel 1",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        customBodyRender: (value, tableMeta) => {
          return <span>{value ? value : ""}</span>;
        }
      }
    },
    {
      name: "level2.name",
      label: "Nivel 2",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: true,
        viewColumns: false,
        customBodyRender: (value, tableMeta) => {
          return <span>{value ? value : ""}</span>;
        }
      }
    },
    {
      name: "cae",
      label: "CAE",
      options: {
        filter: true,
        filterType: "textField",
        sort: false,
        display: false
      }
    },
    {
      name: "can_edit",
      label: "",
      options: {
        filter: false,
        filterType: "textField",
        sort: false,
        display: false,
        viewColumns: false
      }
    },
    {
      name: "sap_error",
      label: "Error SAP",
      options: {
        filter: false,
        sort: false,
        display: false
      }
    },
    {
      name: "Acciones",
      label: "",
      options: {
        filter: false,
        setCellHeaderProps: () => ({ align: "center" }),
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowId = tableMeta.rowData[0];
          const invoiceId = tableMeta.rowData[1];
          //const rowId = 1
          return (
            <div className="row" style={{ display: "block" }}>
              <Fab
                color="primary"
                aria-label="view"
                variant="extended"
                style={{ boxShadow: "none", backgroundColor: "green" }}
                fontSize="small"
                className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                href={"credit-note/" + rowId + "/" + invoiceId}
              >
                <EditIcon fontSize="small" />
              </Fab>
            </div>
          );
        }
      }
    }
  ];

  let options = {
    serverSide: true,
    count: count,
    page: page,
    rowsPerPageOptions: [10, 25, 50, 100, rowsPerPage],
    onChangePage: setPage,
    onChangeRowsPerPage: number => setRowsPerPage(number),
    onColumnSortChange: (column, direction) => {
      setOrderColumn(column);
      setOrderOrder(direction);
    },
    customToolbar: () => {
      return (
        <Tooltip title={"Nuevo"}>
          <IconButton
            onClick={e => {
              window.location.href = "/credit-note/0/0";
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      );
    },
    customFilterDialogFooter: filterList => {
      return (
        <div style={{ marginTop: "40px" }}>
          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              setFilters(filterList);
            }}
          >
            Aplicar
          </Button>
        </div>
      );
    },

    textLabels: {
      body: {
        noMatch: isLoading ? (
          <CircularProgress size={24} />
        ) : (
          "No hay datos disponibles"
        )
      }
    }
  };
  if (isLoading) {
    return (
      <div>
        <Typography variant="h6">Cargando...</Typography>
        <LinearProgress />
      </div>
    );
  }
  if (!isLoading) options.rowsPerPage = rowsPerPage;
  return (
    <>
      <MuiThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          title="Notas de crédito"
          data={data}
          columns={columns}
          options={options}
        />
      </MuiThemeProvider>
    </>
  );
};

export default CreditNotes;
